import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { forkJoin, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import {
  OrganizationService,
  OnboardApplicationInput,
  OrgTerminologyModel,
  OrgBrandingModel,
} from "src/app/Services/organization.service";
import { OrgContextService } from "src/app/Services/org-context.service";
import { environment } from "src/environments/environment";
import {
  OrgAccessRoleDefinition,
  OrgRoleFeatureFlags,
  ORG_ROLE_FEATURE_META,
  createDefaultAccessRoles,
  normalizeAccessRoles,
} from "src/app/shared/org-access-roles";
import {
  getLoggedInUser,
  hasBusinessOrganizationAccess,
} from "src/app/shared/utils/plan.util";

@Component({
  selector: "app-org-onboarding",
  templateUrl: "./org-onboarding.component.html",
  styleUrls: ["./org-onboarding.component.scss"],
})
export class OrgOnboardingComponent implements OnInit {
  step = 1;
  /** Furthest step opened so far; earlier steps count as done and stay clickable. */
  maxStepReached = 1;
  @ViewChild("stepStrip") stepStrip?: ElementRef<HTMLElement>;
  saving = false;
  loadingExisting = false;
  isEditing = false;
  organizationId: string | null = null;
  /** First-time create blocked for non-Business users. */
  createBlocked = false;

  organizationName = "";
  /** The creator (or the existing primary admin when editing); no longer typed in. */
  primaryAdminEmail = "";
  /** Optional second admin, invited with the creator's OrgAdmin access once setup completes. */
  addAlternateAdmin = false;
  alternateAdminEmail = "";

  applications: OnboardApplicationInput[] = [
    {
      name: "",
      adminEmail: "",
      teams: [{ name: "", adminEmail: "" }],
    },
  ];

  terminology: OrgTerminologyModel = {
    roleLabel: "Role",
    statusLabel: "Status",
    customRulesLabel: "Rules",
    departmentLabel: "Department",
    locationLabel: "Location",
    divisionLabel: "Division",
    shiftLabel: "Shift",
    applicationLabel: "Application",
    teamLabel: "Team",
    roles: [],
    statuses: [],
    customRules: [],
    departments: [],
    locations: [],
    divisions: [],
    shifts: [],
    accessRoles: createDefaultAccessRoles(),
  };

  /** Editable access-role matrix (bound in Roles step). */
  accessRoles: OrgAccessRoleDefinition[] = createDefaultAccessRoles();
  readonly featureMeta = ORG_ROLE_FEATURE_META;
  expandedRoleKey: string | null = "OrgAdmin";
  /** Cached feature chips — avoid new arrays every change-detection cycle. */
  roleFeatureSummaries: Record<string, string[]> = {};
  private readonly emptyFeatureLabels: string[] = [];

  branding: OrgBrandingModel = {
    logoUrl: "",
    primaryColor: "",
    accentColor: "",
    darkPrimaryColor: "",
    darkAccentColor: "",
  };

  logoUploading = false;
  readonly defaultBrandBlue = "#5EA1D8";

  resultSummary: string | null = null;
  invitedEmails: string[] = [];

  /** Draft inputs for adding a new option value per category. */
  terminologyDrafts: Record<string, string> = {
    roles: "",
    statuses: "",
    customRules: "",
    departments: "",
    locations: "",
    divisions: "",
    shifts: "",
  };

  readonly stepLabels = [
    "Organization",
    "Applications & Teams",
    "Roles",
    "Terminology",
    "Branding",
    "Done",
  ];

  readonly terminologyLabelFields: {
    key: keyof OrgTerminologyModel;
    label: string;
  }[] = [
    { key: "roleLabel", label: "Role label" },
    { key: "statusLabel", label: "Status label" },
    { key: "customRulesLabel", label: "Rules label" },
    { key: "departmentLabel", label: "Department label" },
    { key: "locationLabel", label: "Location label" },
    { key: "divisionLabel", label: "Division label" },
    { key: "shiftLabel", label: "Shift label" },
    { key: "applicationLabel", label: "Application label" },
    { key: "teamLabel", label: "Team label" },
  ];

  /** Optional value tables the org may define during setup. */
  readonly terminologyValueGroups: {
    valuesKey: keyof OrgTerminologyModel;
    labelKey: keyof OrgTerminologyModel;
    title: string;
    placeholder: string;
    hint: string;
  }[] = [
    {
      valuesKey: "roles",
      labelKey: "roleLabel",
      title: "Job titles",
      placeholder: "e.g. Operator, Supervisor",
      hint: "Job titles people will hold (optional — separate from access roles).",
    },
    {
      valuesKey: "statuses",
      labelKey: "statusLabel",
      title: "Status flows",
      placeholder: "e.g. Draft, In Review, Approved",
      hint: "Status values used in your workflows (optional).",
    },
    {
      valuesKey: "customRules",
      labelKey: "customRulesLabel",
      title: "Custom rules",
      placeholder: "e.g. PPE required, Two-person rule",
      hint: "Custom rule tags your teams will use (optional).",
    },
    {
      valuesKey: "departments",
      labelKey: "departmentLabel",
      title: "Departments",
      placeholder: "e.g. Production, Quality",
      hint: "Department names for this organization (optional).",
    },
    {
      valuesKey: "locations",
      labelKey: "locationLabel",
      title: "Locations",
      placeholder: "e.g. Plant A, Site B",
      hint: "Locations / sites (optional).",
    },
    {
      valuesKey: "divisions",
      labelKey: "divisionLabel",
      title: "Divisions",
      placeholder: "e.g. North, South",
      hint: "Divisions (optional).",
    },
    {
      valuesKey: "shifts",
      labelKey: "shiftLabel",
      title: "Shifts",
      placeholder: "e.g. Day, Night",
      hint: "Shift names (optional).",
    },
  ];

  constructor(
    private organizationService: OrganizationService,
    private orgContext: OrgContextService,
    private toastr: ToastrService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const user = this.readLoggedInUser() || getLoggedInUser();
    if (user?.emailAddress) {
      this.primaryAdminEmail = user.emailAddress;
    }
    this.rebuildFeatureSummaries();
    this.loadExistingOrganization();
  }

  goToPricing(): void {
    this.router.navigate(["/pricing"]);
  }

  trackByRoleKey(_: number, role: OrgAccessRoleDefinition): string {
    return role.key;
  }

  trackByIndex(index: number): number {
    return index;
  }

  toggleRoleExpanded(key: string): void {
    this.expandedRoleKey = this.expandedRoleKey === key ? null : key;
  }

  onRoleEnabledChange(role: OrgAccessRoleDefinition, enabled: boolean): void {
    if (role.required) {
      role.enabled = true;
      return;
    }
    role.enabled = enabled;
  }

  onFeatureToggle(
    role: OrgAccessRoleDefinition,
    feature: keyof OrgRoleFeatureFlags,
    value: boolean
  ): void {
    role.features = { ...role.features, [feature]: value };
    this.rebuildFeatureSummaries();
  }

  private rebuildFeatureSummaries(): void {
    const summaries: Record<string, string[]> = {};
    for (const role of this.accessRoles || []) {
      summaries[role.key] = this.featureMeta
        .filter((f) => role.features?.[f.key])
        .map((f) => f.label);
    }
    this.roleFeatureSummaries = summaries;
  }

  featureLabelsFor(roleKey: string): string[] {
    return this.roleFeatureSummaries[roleKey] || this.emptyFeatureLabels;
  }

  getTerminologyValues(key: keyof OrgTerminologyModel): string[] {
    const list = this.terminology[key];
    return Array.isArray(list) ? (list as string[]) : [];
  }

  addTerminologyValue(valuesKey: keyof OrgTerminologyModel): void {
    const draftKey = String(valuesKey);
    const raw = (this.terminologyDrafts[draftKey] || "").trim();
    if (!raw) return;
    const current = this.getTerminologyValues(valuesKey);
    if (current.some((v) => v.toLowerCase() === raw.toLowerCase())) {
      this.toastr.info(`"${raw}" is already added`);
      this.terminologyDrafts[draftKey] = "";
      return;
    }
    (this.terminology as any)[valuesKey] = [...current, raw];
    this.terminologyDrafts[draftKey] = "";
  }

  removeTerminologyValue(
    valuesKey: keyof OrgTerminologyModel,
    index: number
  ): void {
    const current = this.getTerminologyValues(valuesKey);
    (this.terminology as any)[valuesKey] = current.filter((_, i) => i !== index);
  }

  onTerminologyValueKeydown(
    event: KeyboardEvent,
    valuesKey: keyof OrgTerminologyModel
  ): void {
    if (event.key === "Enter") {
      event.preventDefault();
      this.addTerminologyValue(valuesKey);
    }
  }

  get canAddApplication(): boolean {
    return this.applications.every((app) => this.isApplicationComplete(app));
  }

  canAddTeam(appIndex: number): boolean {
    const app = this.applications[appIndex];
    if (!app) return false;
    if (!app.name?.trim() || !app.adminEmail?.trim()) return false;
    return (app.teams || []).every(
      (t) => !!t.name?.trim() && !!t.adminEmail?.trim()
    );
  }

  addApplication(): void {
    if (!this.canAddApplication) return;
    this.applications.push({
      name: "",
      adminEmail: "",
      teams: [{ name: "", adminEmail: "" }],
    });
  }

  removeApplication(index: number): void {
    if (this.applications.length <= 1) return;
    this.applications.splice(index, 1);
  }

  addTeam(appIndex: number): void {
    if (!this.canAddTeam(appIndex)) return;
    this.applications[appIndex].teams.push({ name: "", adminEmail: "" });
  }

  removeTeam(appIndex: number, teamIndex: number): void {
    if (this.applications[appIndex].teams.length <= 1) return;
    this.applications[appIndex].teams.splice(teamIndex, 1);
  }

  /** The last step ("Done") opens only after setup is saved. */
  private readonly setupStepCount = 5;

  get setupComplete(): boolean {
    return this.maxStepReached > this.setupStepCount;
  }

  /** Share of the five setup steps that are done; 100 once setup is saved. */
  get completionPct(): number {
    if (this.setupComplete) return 100;
    const done = Math.max(0, Math.min(this.maxStepReached - 1, this.setupStepCount));
    return Math.round((done / this.setupStepCount) * 100);
  }

  canOpenStep(stepNumber: number): boolean {
    return stepNumber <= this.maxStepReached;
  }

  isStepDone(stepNumber: number): boolean {
    if (this.setupComplete) return stepNumber <= this.setupStepCount || stepNumber === this.step;
    return stepNumber < this.maxStepReached && stepNumber !== this.step;
  }

  stepLockMessage(stepNumber: number): string {
    if (this.canOpenStep(stepNumber)) return "";
    return stepNumber > this.setupStepCount
      ? "Finish setup to complete this step"
      : "Complete the previous step first";
  }

  goToStep(stepNumber: number): void {
    if (stepNumber === this.step || !this.canOpenStep(stepNumber) || this.saving) return;
    if (stepNumber > this.step) {
      for (let s = this.step; s < stepNumber; s++) {
        if (!this.validateStep(s)) return;
      }
    }
    this.setStep(stepNumber);
  }

  get canGoPrevTab(): boolean {
    return this.step > 1 && !this.saving;
  }

  /** The Done tab only opens by saving, so the arrow stops at the last setup step until then. */
  get canGoNextTab(): boolean {
    if (this.saving) return false;
    if (this.step < this.setupStepCount) return true;
    return this.step === this.setupStepCount && this.setupComplete;
  }

  prevTab(): void {
    if (this.canGoPrevTab) this.back();
  }

  nextTab(): void {
    if (!this.canGoNextTab) return;
    if (this.step < this.setupStepCount) this.next();
    else this.goToStep(this.step + 1);
  }

  next(): void {
    if (!this.validateStep(this.step)) return;
    if (this.step < this.setupStepCount) this.setStep(this.step + 1);
  }

  back(): void {
    if (this.step > 1) this.setStep(this.step - 1);
  }

  private setStep(stepNumber: number): void {
    this.step = stepNumber;
    this.maxStepReached = Math.max(this.maxStepReached, stepNumber);
    setTimeout(() => {
      const active = this.stepStrip?.nativeElement.querySelector(".org-step.active") as HTMLElement | null;
      active?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    });
  }

  private validateStep(stepNumber: number): boolean {
    if (stepNumber === 1) return this.validateOrg();
    if (stepNumber === 2) return this.validateApps();
    return true;
  }

  submit(): void {
    if (!this.validateOrg()) {
      this.setStep(1);
      return;
    }
    if (!this.validateApps()) {
      this.setStep(2);
      return;
    }
    this.saving = true;
    const alternateAdminEmail = this.addAlternateAdmin
      ? this.alternateAdminEmail.trim()
      : "";
    const payload = {
      organizationName: this.organizationName.trim(),
      primaryAdminEmail: this.primaryAdminEmail.trim() || this.currentUserEmail,
      alternateAdminEmail: alternateAdminEmail || undefined,
      applications: this.applications.map((a) => ({
        applicationId: a.applicationId,
        name: a.name.trim(),
        adminEmail: a.adminEmail.trim(),
        teams: a.teams.map((t) => ({
          teamId: t.teamId,
          name: t.name.trim(),
          adminEmail: t.adminEmail.trim(),
        })),
      })),
      terminology: {
        ...this.terminology,
        roles: this.terminology.roles || [],
        statuses: this.terminology.statuses || [],
        customRules: this.terminology.customRules || [],
        departments: this.terminology.departments || [],
        locations: this.terminology.locations || [],
        divisions: this.terminology.divisions || [],
        shifts: this.terminology.shifts || [],
        accessRoles: normalizeAccessRoles(this.accessRoles),
      },
      branding:
        this.branding.logoUrl ||
        this.branding.primaryColor ||
        this.branding.accentColor ||
        this.branding.darkPrimaryColor ||
        this.branding.darkAccentColor
          ? {
              logoUrl: this.branding.logoUrl || undefined,
              primaryColor: this.normalizeHexInput(this.branding.primaryColor),
              accentColor: this.normalizeHexInput(this.branding.accentColor),
              darkPrimaryColor: this.normalizeHexInput(
                this.branding.darkPrimaryColor
              ),
              darkAccentColor: this.normalizeHexInput(
                this.branding.darkAccentColor
              ),
            }
          : undefined,
    };

    const wasEditing = this.isEditing && !!this.organizationId;
    const request$ =
      wasEditing
        ? this.organizationService.updateSetup(this.organizationId!, payload)
        : this.organizationService.onboard(payload);

    request$.subscribe({
      next: (res) => {
        this.saving = false;
        this.organizationId =
          res?.organization?.organizationId || this.organizationId;
        this.isEditing = !!this.organizationId;
        this.resultSummary = `${wasEditing ? "Updated" : "Created"} ${
          res?.organization?.name || "organization"
        } with ${res?.applications?.length || 0} applications and ${
          res?.teams?.length || 0
        } teams.`;
        this.invitedEmails = res?.invitedEmails || res?.InvitedEmails || [];
        this.toastr.success(
          wasEditing
            ? "Organization updated — invites sent to new admins (pending acceptance)"
            : "Organization created — invited admins must accept before joining"
        );
        this.addAlternateAdmin = false;
        this.alternateAdminEmail = "";
        this.setStep(6);
        this.orgContext.load(this.organizationId || undefined).subscribe();
        this.sendDeferredMemberInvites(res);
      },
      error: (err) => {
        this.saving = false;
        const msg = err?.error?.error || err?.message || "Save failed";
        this.toastr.error(msg);
      },
    });
  }

  onTeamMemberCsv(event: Event, appIndex: number, teamIndex: number): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const emails = text
        .split(/\r?\n/)
        .map((l) => l.split(/[,;\t]/)[0]?.trim().replace(/^"|"$/g, ""))
        .filter((e) => e && e.includes("@") && !e.toLowerCase().startsWith("email"));
      const team = this.applications[appIndex]?.teams?.[teamIndex];
      if (!team) return;
      const existing = (team.memberInviteEmails || "")
        .split(/[\n,;]+/)
        .map((e) => e.trim())
        .filter(Boolean);
      team.memberInviteEmails = [...new Set([...existing, ...emails])].join("\n");
      this.toastr.success(`Added ${emails.length} email(s)`);
    };
    reader.readAsText(file);
    input.value = "";
  }

  private sendDeferredMemberInvites(res: any): void {
    const orgId =
      res?.organization?.organizationId || this.organizationId;
    if (!orgId) return;
    const teamsFromApi: any[] = res?.teams || [];
    for (let ai = 0; ai < this.applications.length; ai++) {
      const app = this.applications[ai];
      const appFromApi =
        (res?.applications || []).find(
          (a: any) =>
            (a.name || "").trim().toLowerCase() ===
            (app.name || "").trim().toLowerCase()
        ) || (res?.applications || [])[ai];
      const appId = appFromApi?.applicationId || app.applicationId;
      if (!appId) continue;
      for (let ti = 0; ti < (app.teams || []).length; ti++) {
        const team = app.teams[ti];
        const emails = (team.memberInviteEmails || "")
          .split(/[\n,;]+/)
          .map((e: string) => e.trim())
          .filter((e: string) => e.includes("@"));
        if (!emails.length) continue;
        const teamFromApi =
          teamsFromApi.find(
            (t: any) =>
              t.applicationId === appId &&
              (t.name || "").trim().toLowerCase() ===
                (team.name || "").trim().toLowerCase()
          ) ||
          teamsFromApi.filter((t: any) => t.applicationId === appId)[ti];
        const teamId = teamFromApi?.teamId || team.teamId;
        if (!teamId) continue;
        this.organizationService
          .bulkInvite({
            organizationId: orgId,
            applicationId: appId,
            teamId,
            scope: "Team",
            defaultRole: "Worker",
            invites: emails.map((email: string) => ({ email, role: "Worker" })),
          })
          .subscribe({
            next: (bulk) => {
              const created = bulk?.created || [];
              this.invitedEmails = [
                ...this.invitedEmails,
                ...created.map((c: any) => c.email).filter(Boolean),
              ];
            },
          });
      }
    }
  }

  goHome(): void {
    this.router.navigateByUrl("/");
  }

  goToOrgAdmin(): void {
    this.router.navigateByUrl("/org/admin");
  }

  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const okType =
      file.type.startsWith("image/") ||
      file.type === "image/svg+xml" ||
      /\.svg$/i.test(file.name);
    if (!okType) {
      this.toastr.error("Please upload an image or SVG file");
      input.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.toastr.error("Logo must be under 5 MB");
      input.value = "";
      return;
    }

    this.logoUploading = true;
    const formData = new FormData();
    formData.append("fileData", file, file.name);
    this.organizationService.uploadLogo(formData).subscribe({
      next: (res) => {
        this.logoUploading = false;
        const fileName = res?.fileName || res?.FileName;
        if (!fileName) {
          this.toastr.error("Upload succeeded but no file name returned");
          return;
        }
        this.branding.logoUrl = fileName.startsWith("http")
          ? fileName
          : environment.imageStorageBaseUrl + fileName;
        this.toastr.success("Logo uploaded");
        input.value = "";
      },
      error: () => {
        this.logoUploading = false;
        this.toastr.error("Logo upload failed");
        input.value = "";
      },
    });
  }

  clearLogo(): void {
    this.branding.logoUrl = "";
  }

  onHexBlur(field: keyof OrgBrandingModel): void {
    const raw = (this.branding as any)[field];
    const normalized = this.normalizeHexInput(raw);
    if (raw && !normalized) {
      this.toastr.error("Use a valid hex color like #5EA1D8");
      return;
    }
    if (normalized) {
      (this.branding as any)[field] = normalized;
    }
  }

  private normalizeHexInput(value?: string | null): string | undefined {
    return this.orgContext.normalizeHex(value) || undefined;
  }

  private loadExistingOrganization(): void {
    this.loadingExisting = true;
    this.orgContext
      .load()
      .pipe(
        switchMap(() => {
          const orgId =
            this.orgContext.snapshot.activeOrganization?.organizationId ||
            null;
          if (!orgId) {
            const user = this.readLoggedInUser() || getLoggedInUser();
            const memberships = this.orgContext.snapshot.memberships || [];
            const hasMemberships = memberships.some(
              (m: any) =>
                !m.status || String(m.status).toLowerCase() === "active"
            );
            this.createBlocked =
              !hasMemberships && !hasBusinessOrganizationAccess(user);
            return of(null);
          }
          this.organizationId = orgId;
          this.isEditing = true;
          return forkJoin({
            org: this.organizationService.getOrganization(orgId),
            apps: this.organizationService.listApplications(orgId).pipe(
              catchError(() => of([]))
            ),
            terminology: this.organizationService
              .getTerminology(orgId)
              .pipe(catchError(() => of(null))),
            branding: this.organizationService
              .getBranding(orgId)
              .pipe(catchError(() => of(null))),
          }).pipe(
            switchMap(({ org, apps, terminology, branding }) => {
              const appList = apps || [];
              if (!appList.length) {
                return of({ org, apps: [], teamsByApp: [], terminology, branding });
              }
              return forkJoin(
                appList.map((app: any) =>
                  this.organizationService
                    .listTeams(orgId, app.applicationId)
                    .pipe(catchError(() => of([])))
                )
              ).pipe(
                switchMap((teamsByApp) =>
                  of({ org, apps: appList, teamsByApp, terminology, branding })
                )
              );
            })
          );
        }),
        catchError(() => of(null))
      )
      .subscribe((data) => {
        this.loadingExisting = false;
        if (!data?.org) return;
        // Every setup step already has saved values when editing.
        this.maxStepReached = Math.max(this.maxStepReached, this.setupStepCount);
        this.organizationName = data.org.name || "";
        this.primaryAdminEmail =
          data.org.primaryAdminEmail || this.primaryAdminEmail;
        if (data.terminology) {
          this.terminology = {
            ...this.terminology,
            ...data.terminology,
            roles: data.terminology.roles || [],
            statuses: data.terminology.statuses || [],
            customRules: data.terminology.customRules || [],
            departments: data.terminology.departments || [],
            locations: data.terminology.locations || [],
            divisions: data.terminology.divisions || [],
            shifts: data.terminology.shifts || [],
            accessRoles: normalizeAccessRoles(data.terminology.accessRoles),
          };
          this.accessRoles = normalizeAccessRoles(data.terminology.accessRoles);
          this.rebuildFeatureSummaries();
        }
        if (data.branding) {
          this.branding = {
            logoUrl: data.branding.logoUrl || "",
            primaryColor: data.branding.primaryColor || "",
            accentColor: data.branding.accentColor || "",
            darkPrimaryColor: data.branding.darkPrimaryColor || "",
            darkAccentColor: data.branding.darkAccentColor || "",
            surfaceColor: data.branding.surfaceColor,
          };
        }
        const apps = data.apps || [];
        const teamsByApp = data.teamsByApp || [];
        if (apps.length) {
          this.applications = apps.map((app: any, i: number) => ({
            applicationId: app.applicationId,
            name: app.name || "",
            adminEmail: app.adminEmail || "",
            teams: ((teamsByApp[i] as any[]) || []).map((t) => ({
              teamId: t.teamId,
              name: t.name || "",
              adminEmail: t.adminEmail || "",
            })),
          }));
          this.applications.forEach((app) => {
            if (!app.teams.length) {
              app.teams = [{ name: "", adminEmail: "" }];
            }
          });
        }
      });
  }

  private isApplicationComplete(app: OnboardApplicationInput): boolean {
    if (!app?.name?.trim() || !app?.adminEmail?.trim()) return false;
    return (app.teams || []).every(
      (t) => !!t.name?.trim() && !!t.adminEmail?.trim()
    );
  }

  private validateOrg(): boolean {
    if (!this.organizationName.trim()) {
      this.toastr.error("Organization name is required");
      return false;
    }
    if (this.addAlternateAdmin) {
      const email = this.alternateAdminEmail.trim();
      if (!email) {
        this.toastr.error("Enter the alternate admin's email");
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.toastr.error("Enter a valid email for the alternate admin");
        return false;
      }
      const own = [this.currentUserEmail, this.primaryAdminEmail.trim()]
        .filter(Boolean)
        .map((e) => e.toLowerCase());
      if (own.includes(email.toLowerCase())) {
        this.toastr.error("The alternate admin must be someone other than you");
        return false;
      }
    }
    return true;
  }

  private get currentUserEmail(): string {
    const user = this.readLoggedInUser() || getLoggedInUser();
    return `${user?.emailAddress || user?.EmailAddress || ""}`.trim();
  }

  private validateApps(): boolean {
    for (const app of this.applications) {
      if (!app.name.trim() || !app.adminEmail.trim()) {
        this.toastr.error("Each application needs a name and admin email");
        return false;
      }
      for (const team of app.teams) {
        if (!team.name.trim() || !team.adminEmail.trim()) {
          this.toastr.error("Each team needs a name and admin email");
          return false;
        }
      }
    }
    return true;
  }

  private readLoggedInUser(): any {
    try {
      return JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    } catch {
      return null;
    }
  }
}
